var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { inject, proxyRefs, markRaw, ref, reactive, getCurrentInstance, provide, computed, unref, onActivated, onMounted, queuePostFlushCb, onBeforeUnmount, onBeforeMount, onUpdated, warn, h, setTransitionHooks, callWithAsyncErrorHandling, ErrorCodes, shallowRef, resolveComponent, openBlock, createElementBlock, createElementVNode, createVNode, defineComponent, Transition, withCtx, createBlock, createCommentVNode, customRef, onScopeDispose, TransitionGroup, Fragment, renderList, isRef, onDeactivated, normalizeClass, normalizeStyle } from "vue";
const _UrlHelper = class _UrlHelper {
  constructor(url, localFixedPrefix) {
    __publicField(this, "url");
    __publicField(this, "localFixedPrefix");
    this.url = new URL(url ?? window.location.href);
    this.localFixedPrefix = localFixedPrefix ?? null;
  }
  get path() {
    return this.url.pathname;
  }
  get hash() {
    return this.url.hash;
  }
  get href() {
    return this.url.href;
  }
  get fullPrefix() {
    let prefix = _UrlHelper.fixedPrefix ? _UrlHelper.trim(_UrlHelper.fixedPrefix) : null;
    if (this.localFixedPrefix) {
      if (prefix) {
        prefix += "/" + _UrlHelper.trim(this.localFixedPrefix);
      } else {
        prefix = _UrlHelper.trim(this.localFixedPrefix);
      }
    }
    return prefix;
  }
  setPath(path) {
    this.url.pathname = path;
  }
  setDomain(domain, protocol = "https") {
    this.url.host = domain;
    this.url.protocol = protocol;
  }
  /**
   * Get full path, with the locale removed by default
   * /your-path/test?q=t#hash
   */
  getPath(options) {
    const search = new URL(this.href ?? "/", "https://" + window.location.hostname).search;
    return "/" + this.getParts(options).join("/") + search + this.hash;
  }
  getHostWithProtocol() {
    const url = new URL(this.href ?? "/", "https://" + window.location.hostname);
    return url.protocol + "//" + url.host;
  }
  getFullHref(options) {
    const url = new URL(this.href ?? "/", "https://" + window.location.hostname);
    return url.protocol + "//" + ((options == null ? void 0 : options.host) ?? url.host) + this.getPath(options);
  }
  getParts(options) {
    var _a;
    const parts = ((_a = this.path) == null ? void 0 : _a.substring(1).split("/")) ?? [];
    if (parts.length > 0 && parts[parts.length - 1] === "") {
      parts.pop();
    }
    if (parts.length > 0 && ((options == null ? void 0 : options.removeLocale) === void 0 || (options == null ? void 0 : options.removeLocale) === true) && (parts[0].length == 5 && parts[0].substring(2, 3) === "-" || parts[0].length == 2)) {
      parts.shift();
    }
    if (((options == null ? void 0 : options.removePrefix) === void 0 || (options == null ? void 0 : options.removePrefix) === true) && this.fullPrefix) {
      for (const part of this.fullPrefix.split("/") ?? []) {
        if (parts.length > 0 && parts[0] === part) {
          parts.shift();
        } else {
          break;
        }
      }
    }
    if (options == null ? void 0 : options.appendPrefix) {
      parts.unshift(options.appendPrefix);
    }
    return parts;
  }
  getSearchParams() {
    return this.url.searchParams;
  }
  getHashParams() {
    var _a;
    return new URLSearchParams(
      ((_a = this.hash) == null ? void 0 : _a.substr(1)) ?? ""
      // skip the first char (#)
    );
  }
  clear() {
    this.url = new URL("/", "https://" + window.location.hostname);
  }
  /**
   * override params
   */
  static setSearchParams(params) {
    const helper = new _UrlHelper();
    const url = new URL(helper.getFullHref());
    url.search = params.toString();
    this.setUrl(url.pathname + url.search + url.hash);
  }
  /**
   * override params
   */
  static addSearchParam(key, value) {
    const helper = new _UrlHelper();
    const url = new URL(helper.getFullHref());
    url.searchParams.set(key, value);
    url.search = url.searchParams.toString();
    this.setUrl(url.pathname + url.search + url.hash);
  }
  /**
   * Return a transformed url (adds locale and fixed prefix to it)
   */
  static transformUrl(url, localFixedPrefix) {
    let prefix = this.fixedPrefix ? "/" + _UrlHelper.trim(this.fixedPrefix) : "";
    if (localFixedPrefix) {
      prefix += "/" + _UrlHelper.trim(localFixedPrefix);
    }
    if (this.localePrefix) {
      return "/" + _UrlHelper.trim(this.localePrefix) + prefix + "/" + _UrlHelper.trim(url);
    }
    return prefix + "/" + _UrlHelper.trim(url);
  }
  /**
   * @deprecated
   * setURL, but add locale
   */
  static setUrl(url) {
    console.warn("Used UrlHelper.setUrl", url, "which should be replaced with this.setUrl()");
  }
  static trim(url) {
    return url.replace(/^\/+/, "").replace(/\/+$/, "");
  }
  match(template, params) {
    return matchPath(this.getParts(), this.getSearchParams(), template, params);
  }
};
/** 
 * Use this for the universal fixed prefix
 * 
 * Always remove this prefix when getting an url, and add it when doing setUrl.
 * When you want to host an app in a subdirectory
 * Slashes are added automatically on the sides if needed
*/
__publicField(_UrlHelper, "fixedPrefix", null);
__publicField(_UrlHelper, "localePrefix", null);
__publicField(_UrlHelper, "shared", new _UrlHelper(window.location.href));
/**
 * The original values when loading the page. Do not modify this one.
 */
__publicField(_UrlHelper, "initial", new _UrlHelper(window.location.href));
let UrlHelper = _UrlHelper;
function matchPath(path, query, template, params) {
  const parts = Array.isArray(path) ? path : UrlHelper.trim(path).split("/");
  const templateParts = UrlHelper.trim(template).split("/");
  if (parts.length < templateParts.length) {
    return;
  }
  const resultParams = {};
  for (let index = 0; index < templateParts.length; index++) {
    const templatePart = templateParts[index];
    const part = parts[index];
    if (templatePart !== part) {
      if (templatePart[0] === "@") {
        const param = templatePart.substr(1);
        if (params[param]) {
          resultParams[param] = params[param](part);
          if (typeof resultParams[param] === "number") {
            if (!Number.isInteger(resultParams[param])) {
              return;
            }
          }
          continue;
        }
      }
      return;
    }
  }
  return {
    params: resultParams,
    url: parts.slice(0, templateParts.length).join("/"),
    query
  };
}
function matchQuery() {
}
function templateToUrl(template, params) {
  const templateParts = UrlHelper.trim(template).split("/");
  for (let index = 0; index < templateParts.length; index++) {
    const templatePart = templateParts[index];
    if (templatePart[0] === "@") {
      const param = templatePart.substr(1);
      if (params[param]) {
        templateParts[index] = params[param].toString();
      } else {
        throw new Error("Missing param: " + param);
      }
    }
  }
  return templateParts.slice(0, templateParts.length).join("/");
}
class HistoryManagerStatic {
  constructor() {
    // undoActions: Map<number, (animate: boolean) => void> = new Map();
    __publicField(this, "states", []);
    __publicField(this, "counter", 0);
    __publicField(this, "active", false);
    __publicField(this, "animateHistoryPop", true);
    __publicField(this, "isAdjustingState", false);
    __publicField(this, "manualStateAction", false);
    // Manipulating the history is async and can cause issues when fast calls happen without awaiting the previous one
    __publicField(this, "historyQueue", []);
    __publicField(this, "isQueueRunning", false);
    __publicField(this, "changeUrlTimeout", null);
    __publicField(this, "titleSuffix", "");
    __publicField(this, "pageLoadedAt", Date.now());
    __publicField(this, "listeners", /* @__PURE__ */ new Map());
  }
  addListener(owner, handler) {
    this.listeners.set(owner, handler);
  }
  removeListener(owner) {
    this.listeners.delete(owner);
  }
  callListeners() {
    for (const [_, handler] of this.listeners) {
      handler();
    }
  }
  addToQueue(action) {
    this.historyQueue.push(action);
    if (!this.isQueueRunning) {
      this.runQueue();
    }
  }
  runQueue() {
    this.isQueueRunning = true;
    const action = this.historyQueue.shift();
    if (action) {
      const p = action();
      if (p) {
        p.finally(() => this.runQueue()).catch(console.error);
      } else {
        this.runQueue();
      }
    } else {
      this.isQueueRunning = false;
      this.callListeners();
    }
  }
  go(delta) {
    this.addToQueue(async () => {
      return new Promise((resolve) => {
        this.manualStateAction = true;
        history.go(delta);
        let timer = void 0;
        let called = false;
        const listener = () => {
          if (called)
            return;
          called = true;
          clearTimeout(timer);
          window.removeEventListener("popstate", listener);
          setTimeout(() => {
            this.manualStateAction = false;
            resolve();
          }, 0);
        };
        window.addEventListener("popstate", listener);
        timer = setTimeout(() => {
          console.warn("Timeout while waiting for history.go");
          listener();
        }, 200);
      });
    });
  }
  getStateUrl(index) {
    if (index < 0) {
      return "";
    }
    if (!this.states[index]) {
      return this.getStateUrl(index - 1);
    }
    if (this.states[index].url !== null) {
      return this.states[index].url;
    }
    return this.getStateUrl(index - 1);
  }
  resolveUrl(index) {
    return "/" + UrlHelper.trim(UrlHelper.transformUrl(this.getStateUrl(index)));
  }
  /// Set the current URL without modifying states
  setUrl(url, title, index) {
    if (!this.active) {
      return;
    }
    if (index === void 0 || index === this.counter) {
      const state = this.states[this.states.length - 1];
      const count = state.index;
      if (this.changeUrlTimeout) {
        clearTimeout(this.changeUrlTimeout);
      }
      const didJustLoadPage = Date.now() - this.pageLoadedAt < 1e3 * 5;
      this.changeUrlTimeout = setTimeout(() => {
        if (this.counter !== count || state.url !== url) {
          return;
        }
        this.addToQueue(() => {
          if (this.counter !== count || state.url !== url) {
            return;
          }
          const formattedUrl = this.resolveUrl(count);
          history.replaceState({ counter: count }, "", formattedUrl);
          if (state.title) {
            window.document.title = this.formatTitle(state.title);
          }
        });
      }, didJustLoadPage ? 1e3 : 20);
      state.url = url;
      if (title) {
        state.title = title;
      }
    } else {
      const state = this.states[index];
      if (!state) {
        console.error("Search state with index ", index, "but no such state found");
        return;
      }
      if (state.index !== index) {
        console.error("Search state with index ", index, "but received state with index", state.index);
        return;
      }
      if (state.url !== url)
        ;
      state.url = url;
      if (title) {
        state.title = title;
      }
    }
  }
  // Call this when url formatting or prefix has changed
  updateUrl() {
    if (!this.active) {
      return;
    }
    if (this.changeUrlTimeout) {
      return;
    }
    this.addToQueue(() => {
      const lastState = this.states[this.states.length - 1];
      const formattedUrl = this.resolveUrl(lastState.index);
      history.replaceState({ counter: this.counter }, "", formattedUrl);
    });
  }
  formatTitle(title) {
    return title + (this.titleSuffix ? " | " + this.titleSuffix : "");
  }
  /**
   * Set the saved title for a given state. If that state is the current one, it will also get set immediately
   */
  setTitle(title, index) {
    if (!this.active) {
      return;
    }
    if (index === void 0 || index === this.counter) {
      const state = this.states[this.states.length - 1];
      window.document.title = this.formatTitle(title);
      state.title = title;
    } else {
      const state = this.states[index];
      if (state.index !== index) {
        console.error("Search state with index ", index, "but received state with index", state.index);
        return;
      }
      state.title = title;
    }
  }
  getCurrentState() {
    return this.states[this.counter];
  }
  getState(index) {
    if (index < 0) {
      return null;
    }
    const s = this.states[index];
    if (s && s.index === index) {
      return s;
    }
    return this.getState(index - 1);
  }
  pushState(url, undoAction, options) {
    if (!this.active) {
      return;
    }
    this.counter++;
    if (this.counter !== this.states.length) {
      console.error("Invalid initial history item when pushing state");
    }
    const state = {
      url: url ?? null,
      index: this.counter,
      adjustHistory: true,
      undoAction,
      invalid: false,
      ...options
    };
    this.states.push(state);
    const c = this.counter;
    if (state.adjustHistory) {
      this.addToQueue(() => {
        const formattedUrl = url === void 0 ? void 0 : "/" + UrlHelper.trim(UrlHelper.transformUrl(url));
        history.pushState({ counter: c }, "", formattedUrl);
      });
    } else {
      this.addToQueue(() => {
        history.replaceState({ counter: c }, "", void 0);
      });
    }
  }
  /**
   * Call when an action is performed that breaks back/forward navigation
   */
  invalidateHistory() {
    for (const state of this.states) {
      state.undoAction = null;
      state.invalid = state.index !== this.counter;
    }
  }
  /**
   * Return to a given history point in time, if needed
   */
  returnToHistoryIndex(counter) {
    if (counter > this.counter) {
      console.warn("Performed non-compatible navigation. Probably because side-by-side views navigating");
      this.invalidateHistory();
      for (let i = this.counter + 1; i <= counter; i++) {
        if (i !== this.states.length) {
          console.error("Invalid history item at index", i, "when returning to history index");
        }
        this.states.push({
          index: i,
          adjustHistory: false,
          url: null,
          title: void 0,
          invalid: true,
          undoAction: null
        });
      }
      this.counter = counter;
    }
    if (counter < this.counter) {
      this.counter = counter;
      const deletedStates = this.states.splice(this.counter + 1);
      console.log("Deleted states", deletedStates.length);
      const adjustHistoryCount = deletedStates.filter((state) => state.adjustHistory).length;
      if (adjustHistoryCount > 0) {
        this.go(-adjustHistoryCount);
      }
    }
    if (this.states[this.counter].url) {
      this.setUrl(this.states[this.counter].url, this.states[this.counter].title);
    }
    return this.counter;
  }
  activate() {
    history.scrollRestoration = "manual";
    async function onPopState(event) {
      var _a;
      if (this.isAdjustingState) {
        console.warn("Duplicate popstate");
        return;
      }
      if (this.manualStateAction) {
        return;
      }
      this.isAdjustingState = true;
      const newCounter = (_a = event.state) == null ? void 0 : _a.counter;
      if (newCounter !== void 0) {
        if (newCounter > this.counter) {
          const amount = newCounter - this.counter;
          this.go(-amount);
        } else {
          const animate = this.counter - newCounter == 1 && this.animateHistoryPop;
          this.counter = newCounter;
          const deletedStates = this.states.splice(this.counter + 1).reverse();
          const newState = this.states[this.counter];
          if (newState.invalid) {
            console.warn("Reloading page bacause of invalid history", newState);
            window.location.reload();
            return;
          }
          for (const state of deletedStates) {
            if (state.invalid) {
              console.warn("Reloading page bacause of invalid history", state);
              window.location.reload();
              return;
            }
          }
          for (const state of deletedStates) {
            if (state.undoAction) {
              await state.undoAction(animate);
            } else {
              if (state.adjustHistory) {
                break;
              }
            }
          }
        }
      }
      this.isAdjustingState = false;
    }
    window.addEventListener("popstate", (event) => {
      onPopState.call(this, event).catch(console.error);
    });
    const clickHandler = () => {
      this.pageLoadedAt = 0;
    };
    document.addEventListener("pointerdown", clickHandler, { once: true, passive: true });
    this.active = true;
    if (history.state && history.state.counter !== void 0 && typeof history.state.counter === "number") {
      for (let i = 0; i < history.state.counter; i++) {
        if (i !== this.states.length) {
          console.error("Invalid history item at index", i, "when activating history manager");
        }
        this.states.push({
          index: i,
          adjustHistory: false,
          url: null,
          title: void 0,
          invalid: true,
          undoAction: null
        });
      }
      this.counter = history.state.counter;
    }
    history.replaceState({ counter: this.counter }, "");
    if (this.counter !== this.states.length) {
      console.error("Invalid initial history item when activating history manager");
    }
    this.states.push({
      index: this.counter,
      adjustHistory: false,
      url: null,
      title: void 0,
      invalid: false,
      undoAction: null
    });
  }
}
__publicField(HistoryManagerStatic, "debug", false);
const HistoryManager = new HistoryManagerStatic();
function useCurrentComponent() {
  return inject("navigation_currentComponent", null);
}
function getExposeProxy(instance) {
  if (!instance) {
    return;
  }
  if (!instance.exposed) {
    return;
  }
  if (instance.exposeProxy) {
    return instance.exposeProxy;
  }
  const extendingProxy = instance.proxy;
  instance.exposeProxy = new Proxy(proxyRefs(markRaw(instance.exposed)), {
    get(target, key) {
      if (key in target) {
        return target[key];
      }
      return extendingProxy[key];
    },
    has(target, key) {
      return key in target || key in extendingProxy;
    }
  });
  return instance.exposeProxy;
}
function forAllRoots(root, handler, alreadyProcessed) {
  const handled = alreadyProcessed ?? /* @__PURE__ */ new Set();
  handler(root);
  handled.add(root);
  for (const key in root.properties) {
    const v = root.properties[key];
    if (v instanceof ComponentWithProperties) {
      forAllRoots(v, handler, handled);
    }
    if (Array.isArray(v) && v.length) {
      const lastItem = v[v.length - 1];
      if (lastItem instanceof ComponentWithProperties) {
        forAllRoots(lastItem, handler, handled);
      }
    }
  }
}
const _ComponentWithProperties = class _ComponentWithProperties {
  constructor(component, properties = {}, options) {
    /// Name of component or component Options. Currently no way to force type
    __publicField(this, "component");
    __publicField(this, "properties");
    __publicField(this, "provide");
    __publicField(this, "inheritedDisplayerProvide", {});
    __publicField(this, "inheritedParentProvide", {});
    __publicField(this, "key");
    __publicField(this, "type", null);
    /// Saved vnode of this instance
    __publicField(this, "vnode", null);
    __publicField(this, "unmount", null);
    // Keep the vnode alive when it is removed from the VDOM
    __publicField(this, "keepAlive", false);
    __publicField(this, "isKeptAlive", false);
    /// Cover whole screen. Other style = popup
    __publicField(this, "modalDisplayStyle", "cover");
    // If the display animation should be animated
    __publicField(this, "animated", true);
    __publicField(this, "checkRoutes", false);
    // Setting this will allow the component to check the routes on the next activation
    __publicField(this, "isDismissing", ref(false));
    // Custom state for stack items that need to navigate way without being removed from the dom
    // Hisotry index
    __publicField(this, "historyIndex", null);
    __publicField(this, "forceCanHaveFocus", false);
    this.component = component;
    this.key = _ComponentWithProperties.keyCounter++;
    this.properties = reactive(properties);
    this.provide = (options == null ? void 0 : options.provide) || {};
    this.inheritedDisplayerProvide = (options == null ? void 0 : options.inheritedDisplayerProvide) || {};
    this.inheritedParentProvide = (options == null ? void 0 : options.inheritedParentProvide) || {};
    this.forceCanHaveFocus = (options == null ? void 0 : options.forceCanHaveFocus) || false;
    markRaw(this);
  }
  // Automatically true if it has a history state
  // private static ignoreActivate: ComponentWithProperties | null = null
  setCheckRoutes() {
    forAllRoots(this, (c) => {
      c.checkRoutes = true;
    });
    return this;
  }
  get combinedProvide() {
    return {
      ...this.inheritedParentProvide,
      ...this.inheritedDisplayerProvide,
      // has priority
      ...this.provide
    };
  }
  inheritFromDisplayer(component) {
    this.inheritedDisplayerProvide = {
      ...component.combinedProvide
    };
  }
  inheritFromParent(component) {
    this.inheritedParentProvide = {
      ...component.combinedProvide
    };
  }
  clone() {
    return new _ComponentWithProperties(this.component, this.properties, { provide: this.provide, inheritedParentProvide: this.inheritedParentProvide, inheritedDisplayerProvide: this.inheritedDisplayerProvide });
  }
  beforeMount() {
    if (this.vnode) {
      if (this.isKeptAlive) {
        this.isKeptAlive = false;
        _ComponentWithProperties.keepAliveCounter--;
        if (_ComponentWithProperties.debug)
          console.log("Total components kept alive: " + _ComponentWithProperties.keepAliveCounter);
      } else {
        if (_ComponentWithProperties.debug)
          console.warn("About to mount a component that was not destroyed properly " + this.component.name);
        this.destroy(this.vnode);
      }
    }
  }
  getHistoryIndex() {
    if (this.component)
      return this.historyIndex;
  }
  deleteHistoryIndex() {
    this.historyIndex = null;
  }
  hasHistoryIndex() {
    return this.historyIndex !== null;
  }
  canHaveFocus() {
    return this.hasHistoryIndex() || this.forceCanHaveFocus;
  }
  /**
   * This will get called when the component has been pushed somewhere that should count as a new history state
   */
  assignHistoryIndex() {
    if (!HistoryManager.active) {
      return;
    }
    if (this.historyIndex !== null) {
      this.returnToHistoryIndex();
      return;
    }
    const state = HistoryManager.getCurrentState();
    this.historyIndex = state.index;
    _ComponentWithProperties.historyIndexOwners.set(state.index, this);
  }
  inheritHistoryIndex(index) {
    if (this.historyIndex === null) {
      this.historyIndex = index;
    }
  }
  ownsHistoryIndex() {
    return this.historyIndex !== null && _ComponentWithProperties.historyIndexOwners.get(this.historyIndex) === this;
  }
  overrideUrl(url, title) {
    this.provide.reactive_navigation_url = url;
    this.setUrl(url, title);
  }
  setUrl(url, title) {
    if (this.historyIndex === null) {
      if (_ComponentWithProperties.debug)
        console.warn("Tried calling .setUrl on a component that was never assigned a history index. Check if you displayed this component using .show or .present");
      return;
    }
    if (!HistoryManager.active) {
      return;
    }
    if (!this.ownsHistoryIndex()) {
      return;
    }
    HistoryManager.setUrl(url, title, this.historyIndex);
  }
  setTitle(title) {
    if (!title) {
      return;
    }
    if (this.historyIndex === null) {
      if (_ComponentWithProperties.debug)
        console.warn("Tried calling .setTitle on a component that was never assigned a history index. Check if you displayed this component using .show or .present");
      return;
    }
    if (!HistoryManager.active) {
      return;
    }
    HistoryManager.setTitle(title, this.historyIndex);
  }
  /**
   * This will get called when the user returned to this component
   */
  returnToHistoryIndex() {
    if (!HistoryManager.active) {
      return false;
    }
    const instance = this.componentInstance();
    if (instance == null ? void 0 : instance.returnToHistoryIndex) {
      const worked = instance == null ? void 0 : instance.returnToHistoryIndex();
      if (_ComponentWithProperties.debug)
        console.log("returning to instance that has an instance with custom returnToHistoryIndex method", this.component.name, worked);
      if (worked === true) {
        return true;
      }
    }
    if (this.historyIndex === null) {
      if (_ComponentWithProperties.debug)
        console.warn("Returning to a component that has no history index assigned. Has this component been pushed to a navigation controller properly before returning to it?", this.component.name);
      return false;
    }
    _ComponentWithProperties.historyIndexOwners.set(this.historyIndex, this);
    if (_ComponentWithProperties.debug)
      console.log("New owner of history index ", this.historyIndex, this.component.name);
    HistoryManager.returnToHistoryIndex(this.historyIndex);
    return true;
  }
  componentInstance() {
    var _a, _b, _c, _d;
    if (!((_a = this.vnode) == null ? void 0 : _a.component)) {
      return null;
    }
    return getExposeProxy((_b = this.vnode) == null ? void 0 : _b.component) || ((_d = (_c = this.vnode) == null ? void 0 : _c.component) == null ? void 0 : _d.proxy);
  }
  async shouldNavigateAway() {
    const instance = this.componentInstance();
    if (instance && instance.shouldNavigateAway) {
      const promise = instance.shouldNavigateAway();
      if (typeof promise === "boolean") {
        if (!promise) {
          return false;
        }
      } else if (promise.then && promise.catch) {
        const r = await promise;
        if (!r) {
          return false;
        }
      }
    }
    return true;
  }
  destroy(vnode) {
    if (this.vnode) {
      if (vnode !== this.vnode) {
        if (_ComponentWithProperties.debug)
          console.warn("Received destroy event from old/different vnode", this.vnode, vnode);
        return;
      }
      if (this.keepAlive) {
        this.keepAlive = false;
        if (!this.isKeptAlive) {
          this.isKeptAlive = true;
          _ComponentWithProperties.keepAliveCounter++;
          if (_ComponentWithProperties.debug)
            console.log("Kept component alive " + this.component.name);
          if (_ComponentWithProperties.debug)
            console.log("Total components kept alive: " + _ComponentWithProperties.keepAliveCounter);
        }
        return;
      }
      if (this.isKeptAlive) {
        this.isKeptAlive = false;
        _ComponentWithProperties.keepAliveCounter--;
        if (_ComponentWithProperties.debug)
          console.log("Freed component from alive stack " + this.component.name);
        if (_ComponentWithProperties.debug)
          console.log("Total components kept alive: " + _ComponentWithProperties.keepAliveCounter);
      }
      if (_ComponentWithProperties.debug)
        console.log("Destroyed component " + this.component.name, this.vnode);
      if (this.unmount) {
        this.unmount(this.vnode);
        this.unmount = null;
      } else {
        console.error("No unmount function for component " + this.vnode);
      }
      this.vnode = null;
    }
  }
  setDisplayStyle(style) {
    this.modalDisplayStyle = style;
    return this;
  }
  setAnimated(animated) {
    this.animated = animated;
    return this;
  }
};
// Counter for debugging. Count of components that are kept alive but are not mounted.
__publicField(_ComponentWithProperties, "keepAliveCounter", 0);
__publicField(_ComponentWithProperties, "keyCounter", 0);
__publicField(_ComponentWithProperties, "debug", false);
__publicField(_ComponentWithProperties, "historyIndexOwners", /* @__PURE__ */ new Map());
let ComponentWithProperties = _ComponentWithProperties;
/**
* @vue/shared v3.5.12
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
!!(process.env.NODE_ENV !== "production") ? Object.freeze({}) : {};
!!(process.env.NODE_ENV !== "production") ? Object.freeze([]) : [];
const invokeArrayFns = (fns, ...arg) => {
  for (let i = 0; i < fns.length; i++) {
    fns[i](...arg);
  }
};
const ShapeFlags = {
  "ELEMENT": 1,
  "1": "ELEMENT",
  "FUNCTIONAL_COMPONENT": 2,
  "2": "FUNCTIONAL_COMPONENT",
  "STATEFUL_COMPONENT": 4,
  "4": "STATEFUL_COMPONENT",
  "TEXT_CHILDREN": 8,
  "8": "TEXT_CHILDREN",
  "ARRAY_CHILDREN": 16,
  "16": "ARRAY_CHILDREN",
  "SLOTS_CHILDREN": 32,
  "32": "SLOTS_CHILDREN",
  "TELEPORT": 64,
  "64": "TELEPORT",
  "SUSPENSE": 128,
  "128": "SUSPENSE",
  "COMPONENT_SHOULD_KEEP_ALIVE": 256,
  "256": "COMPONENT_SHOULD_KEEP_ALIVE",
  "COMPONENT_KEPT_ALIVE": 512,
  "512": "COMPONENT_KEPT_ALIVE",
  "COMPONENT": 6,
  "6": "COMPONENT"
};
function invokeVNodeHook(hook, instance, vnode, prevVNode = null) {
  callWithAsyncErrorHandling(hook, instance, ErrorCodes.VNODE_HOOK, [
    vnode,
    prevVNode
  ]);
}
function getInnerChild(vnode) {
  return vnode.shapeFlag & ShapeFlags.SUSPENSE ? vnode.ssContent : vnode;
}
function resetShapeFlag(vnode) {
  vnode.shapeFlag &= ~ShapeFlags.COMPONENT_SHOULD_KEEP_ALIVE;
  vnode.shapeFlag &= ~ShapeFlags.COMPONENT_KEPT_ALIVE;
}
function makeProvidesParentReactive(instance) {
  var _a;
  const reactiveInstance = shallowRef(instance);
  const originalProvides = instance.provides;
  const ownProvides = instance.provides === ((_a = instance.parent) == null ? void 0 : _a.provides) ? [] : Object.getOwnPropertyNames(originalProvides);
  const proxyProvider = new Proxy({}, {
    get(_target, key) {
      if (typeof key !== "string" || !key.startsWith("reactive_")) {
        return originalProvides[key];
      }
      if (key in ownProvides) {
        return originalProvides[key];
      }
      return computed(() => {
        var _a2;
        return unref((_a2 = reactiveInstance.value.parent) == null ? void 0 : _a2.provides[key]);
      });
    },
    // Vue valides keys using 'a' in obj, so we need to handle this correctly
    has(target, key) {
      return true;
    }
  });
  instance.provides = Object.create(proxyProvider);
  return reactiveInstance;
}
const ComponentWithPropertiesInstance = {
  name: "ComponentWithPropertiesInstance",
  props: {
    component: {
      type: ComponentWithProperties,
      required: true
    },
    customProvide: {
      required: false,
      type: Object,
      default: null
    }
  },
  __isKeepAlive: true,
  setup(props) {
    const instance = getCurrentInstance();
    const sharedContext = instance.ctx;
    const reactiveInstance = makeProvidesParentReactive(instance);
    if (props.customProvide) {
      for (const key in props.customProvide) {
        if (key.startsWith("reactive_")) {
          provide(key, computed(() => {
            var _a;
            return unref((_a = props.customProvide) == null ? void 0 : _a[key]);
          }));
        } else {
          provide(key, props.customProvide[key]);
        }
      }
    }
    provide("navigation_currentComponent", props.component);
    const parentHistory = inject("navigation_historyIndex", null);
    if (parentHistory !== null) {
      props.component.inheritHistoryIndex(parentHistory);
    }
    const parent = inject("navigation_parent", null);
    if (parent !== null) {
      props.component.inheritFromParent(parent);
    }
    provide("navigation_historyIndex", props.component.historyIndex);
    provide("navigation_parent", props.component);
    const combinedProvide = props.component.combinedProvide;
    for (const key in combinedProvide) {
      provide(key, combinedProvide[key]);
    }
    const disableUrl = inject("reactive_navigation_disable_url", null);
    const inheritedUrlRaw = inject("reactive_navigation_url", null);
    const updateUrl = () => {
      const url = unref(props.component.combinedProvide.reactive_navigation_url) ?? unref(inheritedUrlRaw) ?? null;
      const disableUrlUnwrapped = unref(disableUrl) ?? false;
      if (!disableUrlUnwrapped) {
        props.component.setUrl(url);
      }
    };
    onActivated(() => {
      updateUrl();
    });
    onMounted(() => {
      updateUrl();
    });
    const {
      renderer: {
        p: patch,
        m: move,
        um: _unmount
      }
    } = sharedContext;
    const parentSuspense = instance.suspense;
    sharedContext.activate = (vnode, container, anchor, namespace, optimized) => {
      const instance2 = vnode.component;
      move(vnode, container, anchor, 0, parentSuspense);
      patch(
        instance2.vnode,
        vnode,
        container,
        anchor,
        instance2,
        parentSuspense,
        namespace,
        vnode.slotScopeIds,
        optimized
      );
      queuePostFlushCb(() => {
        instance2.isDeactivated = false;
        if (instance2.a) {
          invokeArrayFns(instance2.a);
        }
        const vnodeHook = vnode.props && vnode.props.onVnodeMounted;
        if (vnodeHook) {
          invokeVNodeHook(vnodeHook, instance2.parent, vnode);
        }
      });
    };
    sharedContext.deactivate = (vnode) => {
      const instance2 = vnode.component;
      queuePostFlushCb(() => {
        if (instance2.da) {
          invokeArrayFns(instance2.da);
        }
        const vnodeHook = vnode.props && vnode.props.onVnodeUnmounted;
        if (vnodeHook) {
          invokeVNodeHook(vnodeHook, instance2.parent, vnode);
        }
        instance2.isDeactivated = true;
      });
    };
    function unmount(vnode) {
      if (current) {
        const { subTree } = instance;
        const _innerVnode = getInnerChild(subTree);
        if (vnode.type === _innerVnode.type && vnode.key === _innerVnode.key) {
          resetShapeFlag(_innerVnode);
          const da = _innerVnode.component.da;
          da && queuePostFlushCb(da);
          return;
        }
      }
      resetShapeFlag(vnode);
      _unmount(vnode, instance, parentSuspense, true);
    }
    let current = null;
    function getChildVNode() {
      var _a, _b, _c, _d;
      return (_d = (_c = (_b = (_a = instance.vnode) == null ? void 0 : _a.component) == null ? void 0 : _b.subTree) == null ? void 0 : _c.component) == null ? void 0 : _d.vnode;
    }
    onBeforeUnmount(() => {
      if (!current) {
        return;
      }
      const child = getChildVNode();
      if (!child) {
        console.error("No child found in ComponentWithPropertiesInstance.beforeUnmount");
        return;
      }
      if (props.component.unmount === unmount) {
        props.component.destroy(getChildVNode());
      } else {
        console.warn("ComponentWithPropertiesInstance unmount called with different unmount function");
      }
      current = null;
    });
    onBeforeMount(() => {
      props.component.beforeMount();
    });
    let pendingCacheKey = null;
    const cacheSubtree = () => {
      if (current && pendingCacheKey != null) {
        const child = getChildVNode();
        if (!child) {
          console.error("No child found in ComponentWithPropertiesInstance.cacheSubtree");
          return;
        }
        props.component.vnode = child;
        props.component.unmount = unmount;
        if (!child._reactiveInstance) {
          child._reactiveInstance = reactiveInstance;
        }
      }
    };
    onMounted(cacheSubtree);
    onUpdated(cacheSubtree);
    return () => {
      pendingCacheKey = null;
      if (!props.component) {
        warn("No component provided to ComponentWithPropertiesInstance");
        current = null;
        return null;
      }
      props.component.component.inheritAttrs = false;
      const vnode = h(props.component.component, props.component.properties);
      const comp = vnode.type;
      const key = vnode.key == null ? comp : vnode.key;
      pendingCacheKey = key;
      if (props.component.vnode) {
        const cachedVNode = props.component.vnode;
        const parent2 = instance;
        vnode.el = cachedVNode.el;
        vnode.component = cachedVNode.component;
        if (!cachedVNode._reactiveInstance) {
          console.warn("Missing _reactiveInstance on vnode");
        } else {
          cachedVNode._reactiveInstance.value = parent2;
        }
        if (vnode.transition) {
          setTransitionHooks(vnode, vnode.transition);
        }
        vnode.shapeFlag |= ShapeFlags.COMPONENT_KEPT_ALIVE;
      }
      vnode.shapeFlag |= ShapeFlags.COMPONENT_SHOULD_KEEP_ALIVE;
      current = vnode;
      return vnode;
    };
  }
};
const _sfc_main$5 = {
  name: "FramedComponent",
  components: {
    ComponentWithPropertiesInstance
  },
  provide() {
    return {
      ...this.customProvide ?? {}
    };
  },
  props: {
    root: {
      type: ComponentWithProperties,
      required: true
    },
    customProvide: {
      type: Object,
      default: null
    }
  },
  emits: ["show", "push", "pop"]
};
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
function _sfc_render$2(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_ComponentWithPropertiesInstance = resolveComponent("ComponentWithPropertiesInstance");
  return openBlock(), createElementBlock("div", null, [
    createElementVNode("div", null, [
      createVNode(_component_ComponentWithPropertiesInstance, { component: $props.root }, null, 8, ["component"])
    ])
  ]);
}
const FramedComponent = /* @__PURE__ */ _export_sfc(_sfc_main$5, [["render", _sfc_render$2]]);
function useNavigationController() {
  const c = inject("reactive_navigationController");
  return shallowRef(c);
}
const NavigationController$1 = defineComponent({
  name: "NavigationController",
  components: {
    FramedComponent
  },
  inject: {
    reactive_navigation_pop: {
      default: null
    },
    reactive_navigation_can_pop: {
      default: false
    },
    reactive_navigation_can_dismiss: {
      default: false
    }
  },
  provide() {
    let extra = {};
    if (this.animationType === "modal") {
      extra = {
        reactive_navigation_dismiss: computed(() => this.components.length > 1 ? this.pop : unref(this.reactive_navigation_pop)),
        reactive_navigation_can_dismiss: computed(() => this.components.length > 1),
        reactive_navigation_can_pop: false
      };
    } else {
      extra = {
        reactive_navigation_can_pop: computed(() => this.components.length > 1 || unref(this.reactive_navigation_can_pop))
      };
    }
    return {
      reactive_navigationController: this,
      reactive_navigation_show: this.push,
      reactive_navigation_pop: computed(() => this.components.length > 1 ? this.pop : unref(this.reactive_navigation_pop)),
      ...extra,
      ...this.customProvide ?? {}
    };
  },
  props: {
    root: {
      default: null,
      type: Object
    },
    initialComponents: {
      default: null,
      type: Object
    },
    animationType: {
      default: "default",
      type: String
    },
    customProvide: {
      type: Object,
      default: null
    }
  },
  emits: ["didPush", "didPop", "showDetail", "present"],
  data() {
    const savedInternalScrollPositions = [];
    const savedScrollPositions = [];
    const components = [];
    return {
      components,
      mainComponent: null,
      transitionName: "none",
      savedScrollPositions,
      nextScrollPosition: 0,
      previousScrollPosition: 0,
      nextInternalScrollPosition: 0,
      savedInternalScrollPositions,
      // Because push/pop is async, it can cause issues if we call it multiple times after each other
      // all push/pop operations should be queued
      asyncQueue: [],
      asyncQueueRunning: false,
      cachedProvides: /* @__PURE__ */ new Map()
    };
  },
  watch: {
    // whenever question changes, this function will run
    mainComponent(newComponent) {
      if (!newComponent) {
        return;
      }
      this.cacheComponentProvides(newComponent);
    }
  },
  beforeMount() {
    if (this.initialComponents && this.initialComponents.length > 0) {
      this.mainComponent = this.initialComponents[this.initialComponents.length - 1];
      this.components = this.initialComponents.slice(0);
    } else {
      if (!this.root) {
        throw new Error("No root component provided for navigation controller");
      }
      this.mainComponent = this.root;
      this.components = [this.root];
    }
    for (const [index, component] of this.components.entries()) {
      this.cacheComponentProvides(component);
      if (index < this.components.length - 1) {
        HistoryManager.pushState(void 0, null, { adjustHistory: false });
      }
      component.assignHistoryIndex();
    }
  },
  beforeUnmount() {
    for (const component of this.components) {
      if (component.isKeptAlive && component.vnode) {
        component.destroy(component.vnode);
      }
    }
    this.components = [];
    this.mainComponent = null;
  },
  methods: {
    cacheComponentProvides(newComponent) {
      if (this.animationType === "modal") {
        this.cachedProvides.set(newComponent.key, {
          reactive_navigation_can_dismiss: this.components.length > 1
        });
      } else {
        this.cachedProvides.set(newComponent.key, {
          reactive_navigation_can_pop: this.components.length > 1 || unref(this.reactive_navigation_can_pop)
        });
      }
    },
    provideForComponent(key) {
      return this.cachedProvides.get(key) ?? {};
    },
    runQueue(run) {
      return new Promise((resolve, reject) => {
        this.asyncQueue.push(async () => {
          try {
            const r = await run();
            resolve(r);
          } catch (e) {
            reject(e);
          }
        });
        this.runQueueIfNeeded();
      });
    },
    runQueueIfNeeded() {
      if (this.asyncQueueRunning) {
        return;
      }
      if (this.asyncQueue.length == 0) {
        return;
      }
      this.asyncQueueRunning = true;
      const next = this.asyncQueue.shift();
      if (!next) {
        this.asyncQueueRunning = false;
        this.runQueueIfNeeded();
        return;
      }
      next().catch((e) => {
        console.error("Error in async queue", e);
      }).finally(() => {
        this.asyncQueueRunning = false;
        this.runQueueIfNeeded();
      });
    },
    freezeSize() {
      const el = this.$el;
      const w = el.offsetWidth;
      const h2 = el.offsetHeight;
      el.style.width = w + "px";
      el.style.height = h2 + "px";
    },
    growSize(width, height) {
      const el = this.$el;
      el.style.height = height + "px";
      el.style.width = width + "px";
    },
    unfreezeSize() {
      const el = this.$el;
      el.style.width = "";
      el.style.height = "";
    },
    getInternalScrollElement(element = null) {
      var _a;
      const mightBe = (_a = element ?? this.$el) == null ? void 0 : _a.querySelector("main");
      return mightBe ? mightBe : null;
    },
    getScrollElement(element = null) {
      return document.documentElement;
    },
    shouldAnimate() {
      return this.$el && this.$el.offsetWidth <= 1e3 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    },
    returnToHistoryIndex() {
      const lastComponent = this.components[this.components.length - 1];
      if (lastComponent && lastComponent.hasHistoryIndex()) {
        return lastComponent.returnToHistoryIndex();
      }
      return false;
    },
    async push(options) {
      return await this.runQueue(async () => {
        var _a;
        if (options.components.length == 0) {
          console.error("Missing component when pushing");
          return;
        }
        (_a = document.activeElement) == null ? void 0 : _a.blur();
        const components = options.components;
        const component = components[components.length - 1];
        const destroy = options.destroy ?? true;
        const force = options.force ?? false;
        const animated = this.shouldAnimate() ? options.animated === void 0 ? component.animated : options.animated : false;
        let replace = options.replace ?? 0;
        if (replace > this.components.length) {
          replace = this.components.length;
        }
        if (ComponentWithProperties.debug)
          console.log("Pushing new component on navigation controller: " + component.component.name);
        if (replace > 0) {
          if (destroy && !force) {
            for (let index = this.components.length - 1; index >= this.components.length - replace; index--) {
              const component2 = this.components[index];
              const r = await component2.shouldNavigateAway();
              if (!r) {
                return;
              }
            }
          }
        }
        if (!animated) {
          this.transitionName = "none";
        } else {
          this.transitionName = this.animationType == "modal" ? "modal-push" : options.reverse ? "pop" : "push";
        }
        const internalScrollElement = this.getInternalScrollElement();
        const w = window;
        let clientHeight = document.documentElement.clientHeight;
        if (w.visualViewport) {
          clientHeight = w.visualViewport.height;
        }
        const internalClientHeight = internalScrollElement == null ? void 0 : internalScrollElement.clientHeight;
        this.previousScrollPosition = 0;
        this.savedScrollPositions.push(this.previousScrollPosition + clientHeight);
        this.savedInternalScrollPositions.push(((internalScrollElement == null ? void 0 : internalScrollElement.scrollTop) ?? 0) + (internalClientHeight ?? 0));
        this.nextScrollPosition = 0;
        this.nextInternalScrollPosition = 0;
        if (animated) {
          this.freezeSize();
        }
        if (replace > 0) {
          const popped = this.components.splice(this.components.length - replace, replace, ...components);
          if (!destroy) {
            for (const comp of popped) {
              comp.keepAlive = true;
            }
          }
          if (this.components.length !== components.length) {
            HistoryManager.invalidateHistory();
          }
        } else {
          this.components.push(...components);
        }
        if (this.mainComponent) {
          this.mainComponent.keepAlive = !replace;
        }
        const adjustHistory = (options == null ? void 0 : options.adjustHistory) ?? true;
        if (adjustHistory) {
          for (const component2 of components) {
            HistoryManager.pushState(void 0, async (canAnimate) => {
              if (!this.mainComponent) {
                console.error("Tried to pop NavigationController, but it was already unmounted");
                return;
              }
              await this.pop({ animated: animated && canAnimate });
            }, {
              adjustHistory,
              invalid: options.invalidHistory ?? !!replace
            });
            component2.assignHistoryIndex();
          }
        } else {
          for (const component2 of components) {
            if (!replace || this.components.length !== components.length) {
              HistoryManager.pushState(void 0, null, {
                adjustHistory,
                invalid: options.invalidHistory ?? !!replace
              });
            }
            component2.assignHistoryIndex();
          }
        }
        this.mainComponent = component;
        this.$emit("didPush");
        await this.$nextTick();
      });
    },
    async shouldNavigateAway() {
      for (let index = this.components.length - 1; index >= 0; index--) {
        const component = this.components[index];
        const r = await component.shouldNavigateAway();
        if (!r) {
          return false;
        }
      }
      return true;
    },
    popToRoot(options = {}) {
      options.count = this.components.length - 1;
      return this.pop(options);
    },
    getPoppableParent() {
      let prev = this.$;
      let start = this.$.parent;
      while (start) {
        if (prev.props.onPop) {
          return prev;
        }
        prev = start;
        start = start.parent;
      }
      return null;
    },
    async pop(options = {}) {
      return await this.runQueue(async () => {
        var _a;
        (_a = document.activeElement) == null ? void 0 : _a.blur();
        const animated = this.shouldAnimate() ? options.animated ?? true : false;
        const destroy = options.destroy ?? true;
        const count = options.count ?? 1;
        const force = options.force ?? false;
        if (this.components.length <= count) {
          const parentPop = unref(this.reactive_navigation_pop);
          options.count = 1;
          if (!parentPop) {
            console.error("Tried to pop an empty navigation controller, but couldn't find a parent to pop");
            return;
          }
          return await parentPop(options);
        }
        if (count === 0) {
          return;
        }
        if (destroy && !force) {
          for (let index = this.components.length - 1; index >= this.components.length - count; index--) {
            const component = this.components[index];
            const r = await component.shouldNavigateAway();
            if (!r) {
              return;
            }
          }
        }
        this.previousScrollPosition = 0;
        if (!animated) {
          this.transitionName = "none";
        } else {
          this.transitionName = this.animationType == "modal" ? "modal-pop" : "pop";
          this.freezeSize();
        }
        const popped = this.components.splice(this.components.length - count, count);
        if (!destroy) {
          for (const comp of popped) {
            comp.keepAlive = true;
          }
        }
        this.nextScrollPosition = 0;
        this.nextInternalScrollPosition = Math.max(0, this.savedInternalScrollPositions.pop() ?? 0);
        this.mainComponent = this.components[this.components.length - 1];
        this.mainComponent.returnToHistoryIndex();
        await this.$nextTick();
        this.$emit("didPop");
        return popped;
      });
    },
    beforeEnter(insertedElement) {
      if (this.transitionName == "none") {
        return;
      }
      insertedElement.className = this.transitionName + "-enter-active " + this.transitionName + "-enter-from";
    },
    beforeLeave(leavingElement) {
      if (this.transitionName == "none") {
        return;
      }
      leavingElement.className = this.transitionName + "-leave-active ";
    },
    beforeBeforeEnterAnimation() {
      if (this.mainComponent) {
        const instance = this.mainComponent.componentInstance();
        if (instance && instance.beforeBeforeEnterAnimation) {
          instance.beforeBeforeEnterAnimation();
        }
      }
    },
    finishedEnterAnimation() {
      if (this.mainComponent) {
        const instance = this.mainComponent.componentInstance();
        if (instance && instance.finishedEnterAnimation) {
          instance.finishedEnterAnimation();
        }
      }
    },
    enter(element, done) {
      if (this.transitionName == "none") {
        this.getScrollElement().scrollTop = this.nextScrollPosition;
        const internal = this.getInternalScrollElement(element);
        if (internal) {
          internal.scrollTop = Math.max(0, this.nextInternalScrollPosition - internal.clientHeight);
        }
        done();
        return;
      }
      requestAnimationFrame(() => {
        const w = element.firstElementChild.firstElementChild.offsetWidth;
        const h2 = element.firstElementChild.offsetHeight;
        const internal = this.getInternalScrollElement(element);
        let nextInternal = this.nextInternalScrollPosition;
        if (internal) {
          nextInternal = Math.max(0, this.nextInternalScrollPosition - internal.clientHeight);
          const scrollOuterHeight = this.getScrollOuterHeight(internal);
          const h22 = internal.scrollHeight;
          if (nextInternal > h22 - scrollOuterHeight) {
            nextInternal = Math.max(0, h22 - scrollOuterHeight);
          }
        }
        const childElement = element.firstElementChild;
        let transitionDuration = 300;
        if (this.transitionName === "pop" || this.transitionName == "modal-pop") {
          transitionDuration = 250;
        }
        if (this.transitionName == "push" || this.transitionName == "pop" || this.transitionName == "modal-push") {
          childElement.style.willChange = "transform";
        }
        if (internal) {
          internal.style.willChange = "scroll-position";
        }
        this.growSize(w, h2);
        requestAnimationFrame(() => {
          if (internal) {
            internal.scrollTop = nextInternal;
          }
          element.className = this.transitionName + "-enter-active " + this.transitionName + "-enter-to";
          setTimeout(() => {
            element.style.willChange = "";
            childElement.style.willChange = "";
            if (internal) {
              internal.style.willChange = "";
            }
            done();
          }, transitionDuration + 25);
        });
      });
    },
    getScrollOuterHeight(scrollElement) {
      let h2 = scrollElement.clientHeight;
      if (scrollElement === document.documentElement) {
        const w = window;
        if (w.visualViewport) {
          h2 = w.visualViewport.height;
        }
      }
      return h2;
    },
    leave(element, done) {
      if (this.transitionName == "none") {
        done();
        return;
      }
      const childElement = element.firstElementChild;
      childElement.style.willChange = "transform";
      let transitionDuration = 300;
      if (this.transitionName === "pop" || this.transitionName == "modal-pop") {
        transitionDuration = 250;
      }
      requestAnimationFrame(() => {
        const h2 = this.$el.offsetHeight;
        const w = this.$el.offsetWidth;
        const height = h2 + "px";
        const width = w + "px";
        element.className = this.transitionName + "-leave-active " + this.transitionName + "-leave-from";
        element.style.top = "0px";
        element.style.height = height;
        element.style.width = width;
        element.style.bottom = "auto";
        element.style.overflow = "hidden";
        childElement.style.overflow = "hidden";
        childElement.style.height = height;
        childElement.style.width = width;
        requestAnimationFrame(() => {
          element.className = this.transitionName + "-leave-active " + this.transitionName + "-leave-to";
          setTimeout(() => {
            element.style.overflow = "";
            element.style.top = "";
            element.style.height = "";
            element.style.bottom = "";
            childElement.style.overflow = "";
            childElement.style.willChange = "";
            done();
          }, transitionDuration + 25);
        });
      });
    },
    afterLeave(element) {
      if (this.transitionName == "none") {
        return;
      }
      element.className = "";
    },
    afterEnter(element) {
      if (this.transitionName == "none") {
        return;
      }
      this.unfreezeSize();
      element.className = "";
    },
    enterCancelled(_element) {
      this.unfreezeSize();
    }
  }
});
const _sfc_main$4 = NavigationController$1;
const _hoisted_1$3 = {
  key: 0,
  class: "navigation-controller"
};
function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_FramedComponent = resolveComponent("FramedComponent");
  return _ctx.mainComponent ? (openBlock(), createElementBlock("div", _hoisted_1$3, [
    createVNode(Transition, {
      css: false,
      onBeforeEnter: _ctx.beforeEnter,
      onBeforeLeave: _ctx.beforeLeave,
      onEnter: _ctx.enter,
      onLeave: _ctx.leave,
      onAfterLeave: _ctx.afterLeave,
      onAfterEnter: _ctx.afterEnter,
      onEnterCancelled: _ctx.enterCancelled
    }, {
      default: withCtx(() => [
        (openBlock(), createBlock(_component_FramedComponent, {
          key: _ctx.mainComponent.key,
          ref: "child",
          root: _ctx.mainComponent,
          "custom-provide": _ctx.provideForComponent(_ctx.mainComponent.key)
        }, null, 8, ["root", "custom-provide"]))
      ]),
      _: 1
    }, 8, ["onBeforeEnter", "onBeforeLeave", "onEnter", "onLeave", "onAfterLeave", "onAfterEnter", "onEnterCancelled"])
  ])) : createCommentVNode("", true);
}
const NavigationController = /* @__PURE__ */ _export_sfc(_sfc_main$4, [["render", _sfc_render$1]]);
function usePop() {
  const rawPop = inject("reactive_navigation_pop", null);
  return (options) => {
    const pop = unref(rawPop);
    if (!pop) {
      console.warn("Failed to perform pop");
      return;
    }
    return pop(options);
  };
}
function useNavigate() {
  const instance = getCurrentInstance();
  const present = usePresent();
  const show = useShow();
  const showDetail = useShowDetail();
  const toRoute = async function(route, options) {
    let componentProperties = (options == null ? void 0 : options.properties) ?? (options == null ? void 0 : options.params) ?? {};
    let params = (options == null ? void 0 : options.params) ?? {};
    if (!(options == null ? void 0 : options.properties) && route.paramsToProps) {
      componentProperties = route.paramsToProps(params, options == null ? void 0 : options.query);
    }
    if (!(options == null ? void 0 : options.params) && route.propsToParams && componentProperties) {
      const { params: p } = route.propsToParams(await componentProperties);
      params = p;
    }
    const url = templateToUrl(route.url, params ?? {});
    if ("handler" in route) {
      await route.handler({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        modalDisplayStyle: "present" in route && typeof route.present === "string" ? route.present : void 0,
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false,
        componentProperties: await componentProperties
      });
      return;
    }
    let component;
    if (typeof route.component === "function" || componentProperties.then) {
      const method = typeof route.component === "function" ? route.component : () => route.component;
      const originalProperties = componentProperties;
      if (!("PromiseComponent" in window)) {
        throw new Error("Required PromiseComponent window variable to make async components work in routes");
      }
      component = window.PromiseComponent;
      componentProperties = {
        promise: async () => {
          const realComponent = await method();
          return new ComponentWithProperties(realComponent, await originalProperties);
        }
      };
    } else {
      component = route.component === "self" ? instance == null ? void 0 : instance.type : route.component;
    }
    if (!component) {
      throw new Error("Component not found");
    }
    if (route.present) {
      await present({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        components: [
          new ComponentWithProperties(NavigationController, {
            root: new ComponentWithProperties(component, componentProperties)
          })
        ],
        modalDisplayStyle: typeof route.present === "string" ? route.present : void 0,
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false
      });
    } else if (route.show === "detail") {
      await showDetail({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        components: [
          new ComponentWithProperties(NavigationController, {
            root: new ComponentWithProperties(component, componentProperties)
          })
        ],
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false
      });
    } else {
      await show({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        components: [
          new ComponentWithProperties(component, componentProperties)
        ],
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false
      });
    }
  };
  const currentRoutes = getCurrentRoutes();
  const toId = async function(urlOrName, options) {
    const route = currentRoutes.value.find((r) => r.name === urlOrName || r.url === urlOrName);
    if (!route) {
      throw new Error("Route " + urlOrName + " not found in " + (instance == null ? void 0 : instance.type.name));
    }
    return await toRoute(route, options);
  };
  return async function(prop1, prop2) {
    if (typeof prop1 === "string") {
      return await toId(prop1, prop2);
    }
    await toRoute(prop1, prop2);
  };
}
function getCurrentRoutes() {
  const instance = getCurrentInstance();
  return customRef(() => {
    return {
      get() {
        return instance._navigationRoutes ?? [];
      },
      set(newValue) {
        instance._navigationRoutes = newValue;
      }
    };
  });
}
function onCheckRoutes(handler) {
  const instance = getCurrentInstance();
  instance._navigationCheckRoutesHandlers = [...instance._navigationCheckRoutes ?? [], handler];
  addCheckRoutesMountedHandler();
}
function onNotCheckRoutes(handler) {
  const instance = getCurrentInstance();
  instance._navigationNotCheckRoutesHandlers = [...instance._navigationNotCheckRoutesHandlers ?? [], handler];
  addCheckRoutesMountedHandler();
}
function addCheckRoutesMountedHandler() {
  const instance = getCurrentInstance();
  const component = useCurrentComponent();
  if (instance._didAddCheckRoutesMountedHandler) {
    return;
  }
  instance._didAddCheckRoutesMountedHandler = true;
  onMounted(async () => {
    if (component && component.checkRoutes) {
      component.checkRoutes = false;
      if ("_navigationCheckRoutesHandlers" in instance && Array.isArray(instance._navigationCheckRoutesHandlers)) {
        for (const handler of instance._navigationCheckRoutesHandlers) {
          if (typeof handler === "function") {
            await handler();
          } else {
            console.error("Invalid checkRoutes handler", handler);
          }
        }
      }
    } else {
      if ("_navigationNotCheckRoutesHandlers" in instance && Array.isArray(instance._navigationNotCheckRoutesHandlers)) {
        for (const handler of instance._navigationNotCheckRoutesHandlers) {
          if (typeof handler === "function") {
            await handler();
          } else {
            console.error("Invalid not checkRoutes handler", handler);
          }
        }
      }
    }
  });
}
function defineRoutes(routes) {
  const urlhelpers = useUrl();
  const navigate = useNavigate();
  const currentRoutes = getCurrentRoutes();
  const provideDefaultHandler = inject("reactive_provide_default_handler", null);
  if (currentRoutes.value.length) {
    console.error("Multiple calls to defineRoutes: routes will get overwritten");
  }
  currentRoutes.value = Array.isArray(routes) ? routes : [];
  async function handleRoutes(routes2) {
    for (const route of routes2) {
      const result = urlhelpers.match(route.url, route.params);
      if (result) {
        try {
          await navigate(route, {
            params: result.params,
            animated: false,
            adjustHistory: false,
            query: result.query,
            checkRoutes: true
          });
          return true;
        } catch (e) {
          console.error("Failed to navigate to route", route, e);
        }
      }
    }
    try {
      if (await defaultHandler({ allowDetail: false })) {
        return true;
      }
    } catch (e) {
      console.error("Failed to navigate to default (non-detail) route", e);
    }
    return false;
  }
  const getDefaultRoute = ({ allowDetail } = { allowDetail: true }) => {
    if (!Array.isArray(routes)) {
      return null;
    }
    return routes.find((route) => route.isDefault && (allowDetail || !("show" in route) || route.show !== "detail")) ?? null;
  };
  const defaultHandler = async ({ allowDetail } = { allowDetail: true }) => {
    const defaultRoute = getDefaultRoute({ allowDetail });
    if (defaultRoute) {
      try {
        await navigate(defaultRoute, {
          ...defaultRoute.isDefault,
          adjustHistory: false,
          checkRoutes: false
        });
        return true;
      } catch (e) {
        console.error("Failed to navigate to default route", defaultRoute, e);
      }
    }
    return false;
  };
  const setDefaultHandler = () => {
    const dr = getDefaultRoute();
    if (dr) {
      const unreffedProvideDefaultHandler = unref(provideDefaultHandler);
      if (unreffedProvideDefaultHandler) {
        unreffedProvideDefaultHandler(defaultHandler);
      }
    }
  };
  onCheckRoutes(async () => {
    if (Array.isArray(routes)) {
      if (await handleRoutes(routes)) {
        setDefaultHandler();
        return;
      }
    } else {
      const extraRoutes = await routes();
      if (Array.isArray(extraRoutes)) {
        if (await handleRoutes(extraRoutes)) {
          setDefaultHandler();
          return;
        }
      } else {
        if (extraRoutes) {
          setDefaultHandler();
          return;
        }
      }
    }
    setDefaultHandler();
  });
  onNotCheckRoutes(async () => {
    await defaultHandler({ allowDetail: false });
    setDefaultHandler();
  });
}
const checkRouteCache = {
  lastUrl: null,
  results: /* @__PURE__ */ new Map()
};
function useCurrentHref() {
  const state = ref(window.location.href);
  const owner = {};
  HistoryManager.addListener(owner, () => {
    state.value = window.location.href;
  });
  onScopeDispose(() => {
    HistoryManager.removeListener(owner);
  });
  return state;
}
function useCheckRoute() {
  const urlhelpers = useUrl();
  const currentRoutes = getCurrentRoutes();
  const instance = getCurrentInstance();
  const currentPath = useCurrentHref();
  const checkMatchResult = function(route, result, options) {
    if (!result) {
      return false;
    }
    if (options) {
      for (const key in options.params) {
        if (result.params[key] !== options.params[key]) {
          return false;
        }
      }
      for (const key in options.query) {
        if (result.query.get(key) !== options.query.get(key)) {
          return false;
        }
      }
      if (!(options == null ? void 0 : options.params) && route.propsToParams && options.properties) {
        const { params } = route.propsToParams(options.properties);
        for (const key in params) {
          if (result.params[key] !== params[key]) {
            return false;
          }
        }
      }
    }
    return true;
  };
  const quickCheckCache = function(id, options) {
    if (checkRouteCache.lastUrl !== currentPath.value) {
      checkRouteCache.results.clear();
      checkRouteCache.lastUrl = currentPath.value;
    } else {
      const result = checkRouteCache.results.get(id);
      if (result) {
        return checkMatchResult(result.route, result.result, options);
      }
    }
  };
  const checkRoute = function(route, options) {
    const result = urlhelpers.matchCurrent(route.url, route.params);
    checkRouteCache.results.set(route.url, { result, route });
    if (route.name) {
      checkRouteCache.results.set(route.name, { result, route });
    }
    return checkMatchResult(route, result, options);
  };
  const checkId = function(urlOrName, options) {
    const result = quickCheckCache(urlOrName, options);
    if (result !== void 0) {
      return result;
    }
    const route = currentRoutes.value.find((r) => r.name === urlOrName || r.url === urlOrName);
    if (!route) {
      throw new Error("Route " + urlOrName + " not found in " + (instance == null ? void 0 : instance.type.name));
    }
    return checkRoute(route, options);
  };
  return function(prop1, prop2) {
    if (typeof prop1 === "string") {
      return checkId(prop1, prop2);
    }
    const result = quickCheckCache(prop1.name || prop1.url, prop2);
    if (result !== void 0) {
      return result;
    }
    return checkRoute(prop1, prop2);
  };
}
function normalizePushOptions(o, currentComponent, urlHelpers) {
  let options;
  if (!o.components) {
    options = { components: [o] };
  } else {
    options = o;
  }
  if (options.checkRoutes) {
    options.components[options.components.length - 1].setCheckRoutes();
  }
  if (currentComponent) {
    for (const component of options.components) {
      component.inheritFromDisplayer(currentComponent);
    }
  } else {
    console.warn("Using show or present outside of a component: Inherited properties will not be set.");
  }
  if (options.url !== void 0) {
    const url = options.url;
    for (const component of options.components) {
      component.provide.reactive_navigation_url = computed(() => url === null ? null : urlHelpers.extendUrl(url, { returnHistory: options.replace ?? 0 }));
    }
  }
  return options;
}
function useManualPresent() {
  const currentComponent = useCurrentComponent();
  const urlHelpers = useUrl();
  return (present, options) => {
    return present(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function useShow() {
  const currentComponent = useCurrentComponent();
  const rawShow = inject("reactive_navigation_show", null);
  const urlHelpers = useUrl();
  return (options) => {
    const show = unref(rawShow);
    if (!show) {
      console.warn("Failed to perform show");
      return Promise.resolve();
    }
    return show(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function useShowDetail() {
  const currentComponent = useCurrentComponent();
  const rawShowDetail = inject("reactive_navigation_show_detail", null);
  const urlHelpers = useUrl();
  return (options) => {
    const showDetail = unref(rawShowDetail);
    if (!showDetail) {
      console.warn("Failed to perform showDetail");
      return Promise.resolve();
    }
    return showDetail(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function usePresent() {
  const currentComponent = useCurrentComponent();
  const rawPresent = inject("reactive_navigation_present", null);
  const urlHelpers = useUrl();
  return async (options) => {
    const present = unref(rawPresent);
    if (!present) {
      console.warn("Failed to perform present");
      return Promise.resolve();
    }
    return present(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function useDismiss() {
  const rawDismiss = inject("reactive_navigation_dismiss", null);
  return (options) => {
    const dismiss = unref(rawDismiss);
    if (!dismiss) {
      console.warn("Failed to perform dismiss");
      return Promise.resolve();
    }
    return dismiss(options);
  };
}
function useCanPop() {
  const rawPop = inject("reactive_navigation_can_pop", false);
  return computed(() => {
    return !!unref(rawPop);
  });
}
function useCanDismiss() {
  const rawDismiss = inject("reactive_navigation_can_dismiss", false);
  return computed(() => !!unref(rawDismiss));
}
function useFocused() {
  const rawFocused = inject("reactive_navigation_focused", true);
  return computed(() => !!unref(rawFocused));
}
function extendUrl(url) {
  const urlHelpers = useUrl();
  provide("reactive_navigation_url", computed(() => {
    return urlHelpers.extendUrl(unref(url));
  }));
}
function setTitleSuffix(title) {
  HistoryManager.titleSuffix = title;
}
function setTitle(title) {
  if (!title) {
    return;
  }
  const urlHelpers = useUrl();
  onActivated(() => {
    urlHelpers.setTitle(title);
  });
}
function setUrl(url, title) {
  const urlHelpers = useUrl();
  onMounted(() => {
    urlHelpers.overrideUrl(url, title);
  });
}
function useUrl() {
  const currentComponent = useCurrentComponent();
  const navigationUrl = inject("reactive_navigation_url", null);
  const disableUrl = inject("reactive_navigation_disable_url", null);
  const historyIndex = inject("navigation_historyIndex", null);
  return {
    getUrl() {
      return unref(navigationUrl) ?? "";
    },
    /**
     * Ideally call this after you've processed all the .match() calls (and awaited async stuff)
     */
    setTitle(title) {
      if (!currentComponent) {
        console.error("No current component while setting title", title);
        return;
      }
      if (unref(disableUrl)) {
        return;
      }
      if (title) {
        currentComponent.setTitle(title);
      }
    },
    extendUrl(url, options = {}) {
      let prefix = this.getUrl();
      if (options.returnHistory) {
        const index = unref(historyIndex);
        if (index !== null && index !== void 0) {
          prefix = HistoryManager.getStateUrl(index - options.returnHistory);
        } else {
          console.error("Failed to get history index");
        }
      }
      if (prefix && prefix !== "/") {
        return prefix + "/" + UrlHelper.trim(url);
      }
      return UrlHelper.trim(url);
    },
    match(template, params) {
      const shared = UrlHelper.shared;
      const helper = new UrlHelper(shared.url, this.getUrl());
      return helper.match(template, params);
    },
    matchCurrent(template, params) {
      const helper = new UrlHelper(void 0, this.getUrl());
      return helper.match(template, params);
    },
    overrideUrl(url, title) {
      if (!currentComponent) {
        console.error("No current component while setting title", title);
        return;
      }
      if (unref(disableUrl)) {
        return;
      }
      currentComponent == null ? void 0 : currentComponent.overrideUrl(url, title);
    }
  };
}
const _hoisted_1$2 = { class: "stack-component" };
const _sfc_main$3 = /* @__PURE__ */ defineComponent({
  __name: "StackComponent",
  emits: ["returnToHistoryIndex"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const children = ref([]);
    const parentIsFocused = useFocused();
    const emit = __emit;
    function getCustomProvide(index, key) {
      return {
        reactive_navigation_pop: () => {
          removeAt(index, key);
        },
        reactive_navigation_can_pop: true,
        reactive_navigation_dismiss: () => {
          console.warn("Avoid calling dismiss in components on the StackComponent, since options are not supported here");
          removeAt(index, key);
        },
        reactive_navigation_can_dismiss: false,
        reactive_navigation_focused: parentIsFocused.value && getFocusedComponent() === children.value[index]
      };
    }
    function removeAt(index, key) {
      if (!children.value[index]) {
        for (const [i, comp] of children.value.entries()) {
          if (comp.key === key) {
            console.warn("Corrected index from " + index + " to " + i);
            index = i;
            break;
          }
        }
      }
      if (children.value[index] !== void 0 && children.value[index].key === key) {
        const hadFocus = children.value[index].hasHistoryIndex();
        children.value.splice(index, 1);
        if (hadFocus) {
          const newFocused = getComponentWithHistory();
          if (!newFocused) {
            emit("returnToHistoryIndex");
          } else {
            newFocused.returnToHistoryIndex();
          }
        }
      } else {
        console.warn("Expected component with key " + key + " at index " + index);
      }
    }
    function getComponentWithHistory() {
      for (let i = children.value.length - 1; i >= 0; i--) {
        if (children.value[i].hasHistoryIndex() && !children.value[i].isDismissing.value) {
          return children.value[i];
        }
      }
      return null;
    }
    function getFocusedComponent() {
      for (let i = children.value.length - 1; i >= 0; i--) {
        if (children.value[i].canHaveFocus() && !children.value[i].isDismissing.value) {
          return children.value[i];
        }
      }
      return null;
    }
    function show(component) {
      children.value.push(component);
    }
    onBeforeUnmount(() => {
      children.value = [];
    });
    __expose({
      show,
      getFocusedComponent,
      removeAt,
      children
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1$2, [
        createVNode(TransitionGroup, {
          name: "show",
          duration: 300
        }, {
          default: withCtx(() => [
            (openBlock(true), createElementBlock(Fragment, null, renderList(children.value, (component, index) => {
              return openBlock(), createBlock(unref(ComponentWithPropertiesInstance), {
                key: component.key,
                component,
                "custom-provide": getCustomProvide(index, component.key)
              }, null, 8, ["component", "custom-provide"]);
            }), 128))
          ]),
          _: 1
        })
      ]);
    };
  }
});
const _sfc_main$2 = /* @__PURE__ */ defineComponent({
  __name: "ModalStackComponent",
  props: {
    root: {},
    initialComponents: { default: null },
    initialPresents: { default: null }
  },
  setup(__props, { expose: __expose }) {
    const props = __props;
    const navigationController = ref(null);
    const stackComponent = ref(null);
    const parentIsFocused = useFocused();
    const instance = getCurrentInstance();
    provide("reactive_modalStackComponent", computed(() => getExposeProxy(instance)));
    provide("reactive_navigation_present", present);
    onMounted(() => {
      var _a;
      if (props.initialPresents) {
        for (const p of props.initialPresents) {
          (_a = navigationController.value) == null ? void 0 : _a.push(p);
        }
      }
    });
    function returnToHistoryIndex() {
      var _a;
      return (_a = navigationController.value) == null ? void 0 : _a.returnToHistoryIndex();
    }
    const navigationFocused = computed(() => {
      var _a;
      return parentIsFocused.value && !((_a = stackComponent.value) == null ? void 0 : _a.getFocusedComponent());
    });
    const customProvide = computed(() => {
      return {
        reactive_navigation_focused: navigationFocused
      };
    });
    async function present(options) {
      var _a, _b, _c, _d, _e;
      const component = options.components[options.components.length - 1];
      if (options.animated !== void 0) {
        component.animated = options.animated;
      }
      const style = options.modalDisplayStyle ?? component.modalDisplayStyle ?? "cover";
      component.setDisplayStyle(style);
      if ((style === "popup" || style === "sheet" || style === "side-view") && ((_a = stackComponent.value) == null ? void 0 : _a.$el).offsetWidth > 800 || style === "sheet" && ((_b = stackComponent.value) == null ? void 0 : _b.$el).offsetWidth > 700) {
        const c = new ComponentWithProperties(_sfc_main, {
          root: component,
          className: options.modalClass ?? style,
          style: options.modalCssStyle ?? void 0
        });
        c.inheritFromDisplayer(component);
        const adjustHistory = (options == null ? void 0 : options.adjustHistory) ?? true;
        HistoryManager.pushState(void 0, adjustHistory ? async (canAnimate) => {
          var _a2;
          await ((_a2 = c.componentInstance()) == null ? void 0 : _a2.pop({ animated: canAnimate }));
        } : null, {
          adjustHistory,
          invalid: options.invalidHistory ?? false
        });
        c.assignHistoryIndex();
        (_c = stackComponent.value) == null ? void 0 : _c.show(c);
        return;
      }
      if (style === "overlay") {
        (_d = stackComponent.value) == null ? void 0 : _d.show(component);
        return;
      }
      await ((_e = navigationController.value) == null ? void 0 : _e.push(options));
    }
    function replace(component, animated = true) {
      var _a;
      const nav = navigationController.value;
      nav == null ? void 0 : nav.push({ components: [component], animated, replace: ((_a = nav == null ? void 0 : nav.components) == null ? void 0 : _a.length) ?? 0 });
    }
    __expose({
      present: shallowRef(present),
      stackComponent,
      replace
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", null, [
        createVNode(NavigationController, {
          ref_key: "navigationController",
          ref: navigationController,
          "animation-type": "modal",
          root: _ctx.root,
          "initial-components": _ctx.initialComponents,
          "custom-provide": customProvide.value
        }, null, 8, ["root", "initial-components", "custom-provide"]),
        createVNode(_sfc_main$3, {
          ref_key: "stackComponent",
          ref: stackComponent,
          onReturnToHistoryIndex: returnToHistoryIndex
        }, null, 512)
      ]);
    };
  }
});
const ModalStackComponentFinderMixin = defineComponent({
  computed: {
    modalStackComponent() {
      let start = this.$parent;
      while (start) {
        if (start instanceof _sfc_main$2) {
          return start;
        }
        start = start.$parent;
      }
      return null;
    }
  }
});
function injectHooks(instanceProxy, definitions) {
  const ctx = instanceProxy.$.ctx;
  for (const key in definitions) {
    if (!isRef(definitions[key])) {
      ctx[key] = definitions[key];
    } else {
      const val = definitions[key];
      Object.defineProperty(ctx, key, {
        enumerable: true,
        configurable: true,
        get: () => {
          return val.value;
        },
        set: () => {
          warn(`Cannot assign to '${key}' of navigation mixin. This is a read-only property.`);
        }
      });
    }
  }
}
const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return function() {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};
function useSplitViewController() {
  const c = inject("reactive_splitViewController");
  return shallowRef(c);
}
const SplitViewController$1 = defineComponent({
  name: "SplitViewController",
  components: {
    NavigationController,
    FramedComponent
  },
  provide() {
    return {
      reactive_splitViewController: this,
      reactive_navigation_show_detail: this.showDetail
    };
  },
  props: {
    root: {
      required: true,
      type: Object
    },
    detailWidth: {
      default: null,
      type: String
    }
  },
  data() {
    return {
      detail: null,
      detailKey: null,
      defaultHandler: null,
      isChangingComponents: false
    };
  },
  computed: {
    masterProvide() {
      return {
        // The master cannot make changes to the url or title if there is a detail
        reactive_navigation_disable_url: computed(() => !!this.detail),
        reactive_provide_default_handler: (defaultHandler) => {
          this.defaultHandler = defaultHandler;
          this.onResize();
        }
      };
    },
    lastIsDetail() {
      var _a, _b;
      return this.detailKey != null && ((_b = (_a = this.navigationController) == null ? void 0 : _a.mainComponent) == null ? void 0 : _b.key) == this.detailKey;
    },
    navigationController() {
      return this.$refs["navigationController"];
    },
    masterElement() {
      return this.$refs["masterElement"];
    }
  },
  created() {
    const definitions = {
      $url: useUrl()
    };
    injectHooks(this, definitions);
  },
  mounted() {
    if (this.detailWidth) {
      this.$el.style.setProperty("--split-view-width", this.detailWidth);
    }
  },
  activated() {
    this.listener = throttle(this.onResize, 100);
    window.addEventListener("resize", this.listener, { passive: true });
    this.onResize();
  },
  deactivated() {
    window.removeEventListener("resize", this.listener, { passive: true });
  },
  methods: {
    returnToHistoryIndex() {
      if (this.detail) {
        return this.detail.returnToHistoryIndex();
      }
      return this.navigationController.returnToHistoryIndex();
    },
    onResize() {
      if (this.shouldCollapse()) {
        if (this.detail) {
          this.collapse().catch(console.error);
        }
      } else {
        if (this.canExpand()) {
          this.expand().catch(console.error);
        }
      }
    },
    getScrollElement(element = null) {
      if (!element) {
        element = this.$el;
      }
      const style = window.getComputedStyle(element);
      if (style.overflowY == "scroll" || style.overflow == "scroll" || style.overflow == "auto" || style.overflowY == "auto") {
        return element;
      } else {
        if (!element.parentElement) {
          return document.documentElement;
        }
        return this.getScrollElement(element.parentElement);
      }
    },
    async shouldNavigateAway() {
      if (this.detail) {
        const r = await this.detail.shouldNavigateAway();
        if (!r) {
          return false;
        }
      }
      if (this.navigationController) {
        return await this.navigationController.shouldNavigateAway();
      }
      return true;
    },
    async showDetail(options) {
      if (this.isChangingComponents) {
        console.error("Show detail called on a splitViewController that is busy");
        return false;
      }
      const component = options.components[options.components.length - 1];
      this.detailKey = component.key;
      this.isChangingComponents = true;
      try {
        if (this.shouldCollapse()) {
          if (this.lastIsDetail || this.detail) {
            console.error("Pushing a detail when a detail is already presented is not allowed");
            this.isChangingComponents = false;
            return false;
          }
          await this.navigationController.push(options);
        } else {
          if (this.detail) {
            const r = await this.detail.shouldNavigateAway();
            if (!r) {
              this.isChangingComponents = false;
              return false;
            }
          }
          this.getScrollElement().scrollTop = 0;
          HistoryManager.pushState(void 0, null, {
            adjustHistory: options.adjustHistory ?? true,
            invalid: options.invalidHistory ?? !!this.detail
          });
          this.detail = component;
          this.detail.assignHistoryIndex();
        }
      } finally {
        this.isChangingComponents = false;
      }
      return true;
    },
    shouldCollapse() {
      return this.$el.offsetWidth < 850;
    },
    async collapse() {
      if (!this.navigationController) {
        console.error("Cannot collapse without navigation controller");
        return;
      }
      if (this.lastIsDetail) {
        console.error("Cannot collapse when the detail is already collaped");
        return;
      }
      if (!this.detail) {
        console.error("Cannot collapse without detail");
        return;
      }
      if (this.isChangingComponents) {
        console.error("Cannot collapse while already isChangingComponents");
        return;
      }
      this.isChangingComponents = true;
      try {
        this.detail.keepAlive = true;
        const detail = this.detail;
        this.detail = null;
        await this.navigationController.push({ components: [detail], animated: false });
        HistoryManager.invalidateHistory();
      } finally {
        this.isChangingComponents = false;
      }
    },
    canExpand() {
      if (!this.navigationController) {
        return false;
      }
      if (this.detail) {
        return false;
      }
      if (this.isChangingComponents) {
        return false;
      }
      if (!this.lastIsDetail) {
        if (this.defaultHandler) {
          return true;
        }
        return false;
      }
      return true;
    },
    async expand() {
      if (!this.navigationController) {
        console.error("Cannot expand without navigation controller");
        return;
      }
      if (this.detail) {
        console.error("Cannot expand when detail is already visible");
        return;
      }
      if (this.isChangingComponents) {
        console.error("Cannot expand while already isChangingComponents");
        return false;
      }
      if (!this.lastIsDetail) {
        if (!this.defaultHandler) {
          console.error("Cannot expand when there is no defaultHandler");
          return;
        }
        HistoryManager.invalidateHistory();
        this.isChangingComponents = false;
        try {
          const succeeded = await this.defaultHandler();
          if (succeeded && !this.detail) {
            console.warn("Did call defaultHandler but no detail was set. Are all mounts properly awaited?");
          }
          if (!succeeded) {
            console.warn("Failed to show default handler");
          }
          console.info("Used default handler for split view controller");
        } finally {
          this.isChangingComponents = false;
        }
        return;
      }
      this.isChangingComponents = true;
      try {
        const popped = await this.navigationController.pop({
          animated: false,
          destroy: false
        });
        if (!popped || popped.length == 0) {
          this.isChangingComponents = false;
          return;
        }
        await this.$nextTick();
        HistoryManager.pushState(void 0, null, {
          adjustHistory: false,
          invalid: true
        });
        this.detailKey = popped[0].key;
        this.detail = popped[0];
        this.detail.assignHistoryIndex();
      } finally {
        this.isChangingComponents = false;
      }
    }
  }
});
const _sfc_main$1 = SplitViewController$1;
const _hoisted_1$1 = ["data-has-detail"];
const _hoisted_2 = {
  ref: "masterElement",
  class: "master"
};
const _hoisted_3 = {
  key: 0,
  class: "detail"
};
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_NavigationController = resolveComponent("NavigationController");
  const _component_FramedComponent = resolveComponent("FramedComponent");
  return openBlock(), createElementBlock("div", {
    class: "split-view-controller",
    "data-has-detail": _ctx.detail ? "true" : "false"
  }, [
    createElementVNode("div", _hoisted_2, [
      createVNode(_component_NavigationController, {
        ref: "navigationController",
        root: _ctx.root,
        "custom-provide": _ctx.masterProvide
      }, null, 8, ["root", "custom-provide"])
    ], 512),
    _ctx.detail ? (openBlock(), createElementBlock("div", _hoisted_3, [
      (openBlock(), createBlock(_component_FramedComponent, {
        key: _ctx.detail.key,
        root: _ctx.detail
      }, null, 8, ["root"]))
    ])) : createCommentVNode("", true)
  ], 8, _hoisted_1$1);
}
const SplitViewController = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["render", _sfc_render]]);
function useModalStackComponent() {
  const c = inject("reactive_modalStackComponent");
  return c;
}
function usePopup() {
  return inject("reactive_popup", null);
}
const navigationMethods = {
  setTitle() {
    var _a;
    const navigationOptions = (_a = this.$options) == null ? void 0 : _a.navigation;
    if (!navigationOptions)
      return;
    const title = navigationOptions.title;
    if (typeof title === "function") {
      this.$url.setTitle(title.call(this));
    } else {
      this.$url.setTitle(title ?? "");
    }
  }
};
const NavigationMixin = {
  created() {
    var _a;
    const definitions = {
      pop: usePop(),
      showDetail: useShowDetail(),
      show: useShow(),
      present: usePresent(),
      dismiss: useDismiss(),
      canPop: useCanPop(),
      canDismiss: useCanDismiss(),
      isFocused: useFocused(),
      emitParents: () => {
        throw new Error("emitParents has been removed and should no longer be needed");
      },
      popup: usePopup(),
      modalStackComponent: useModalStackComponent(),
      navigationController: useNavigationController(),
      splitViewController: useSplitViewController(),
      $url: useUrl(),
      $navigate: useNavigate()
    };
    const navigationOptions = (_a = this.$options) == null ? void 0 : _a.navigation;
    if (navigationOptions || this.customRoutes) {
      defineRoutes((navigationOptions == null ? void 0 : navigationOptions.routes) ?? (this.customRoutes ? this.customRoutes.bind(this) : null) ?? []);
    }
    injectHooks(this, definitions);
  },
  computed: {
    modalNavigationController() {
      return this.modalStackComponent.navigationController;
    }
  },
  activated() {
    navigationMethods.setTitle.call(this);
  }
  // eslint-disable-next-line @typescript-eslint/ban-types
};
const _hoisted_1 = { class: "scrollable-container" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Popup",
  props: {
    root: {},
    className: { default: "popup" },
    style: { default: "" }
  },
  setup(__props, { expose: __expose }) {
    const instance = getCurrentInstance();
    const Popup = instance.type;
    const props = __props;
    const hide = ref(false);
    const modalStackComponent = useModalStackComponent();
    const pop = usePop();
    const mainContent = ref(null);
    const component = useCurrentComponent();
    provide("reactive_navigation_dismiss", async (options) => {
      return await dismiss(options);
    });
    provide("reactive_navigation_pop", async (options) => {
      console.warn("Using .pop() inside a Popup without a NavigationController dismisses the Popup. It is recommended to use .dismiss() instead.");
      return await dismiss(options);
    });
    provide("reactive_navigation_can_pop", false);
    provide("reactive_navigation_can_dismiss", true);
    provide("reactive_popup", computed(() => getExposeProxy(instance)));
    const pushDown = computed(() => {
      var _a, _b;
      const popups = ((_b = (_a = modalStackComponent.value) == null ? void 0 : _a.stackComponent) == null ? void 0 : _b.children.filter((c) => c.component === Popup && (c.properties.className ?? "popup") === (props.className ?? "popup") && !c.isDismissing.value)) ?? [];
      if (popups.length > 0 && popups[popups.length - 1] !== component) {
        if (popups.length > 1 && popups[popups.length - 2] === component) {
          return 1;
        }
        return 2;
      }
      return 0;
    });
    const buildClass = computed(() => {
      const vvv = { "push-down": pushDown.value == 1, "push-down-full": pushDown.value > 1 };
      const j = Object.keys(vvv).filter((p) => !!vvv[p]).join(" ");
      return j + (j ? " " : "") + (props.className ? props.className : "popup") + (props.root.animated ? " animated" : "") + (isFocused.value ? " focused" : "");
    });
    const isFocused = useFocused();
    const onKey = (event) => {
      if (event.defaultPrevented || event.repeat) {
        return;
      }
      if (!isFocused.value) {
        return;
      }
      const key = event.key || event.keyCode;
      if (key === "Escape" || key === "Esc" || key === 27) {
        dismiss().catch(console.error);
        event.preventDefault();
      }
    };
    const shouldNavigateAway = () => {
      return props.root.shouldNavigateAway();
    };
    const dismiss = async (options) => {
      var _a;
      if (hide.value) {
        return;
      }
      if (!(options == null ? void 0 : options.force)) {
        const r = await shouldNavigateAway();
        if (!r) {
          return false;
        }
      }
      component.isDismissing.value = true;
      (_a = pop(options)) == null ? void 0 : _a.catch(console.error);
    };
    const onClick = (event) => {
      if (mainContent.value && !mainContent.value.contains(event.target) && document.body.contains(event.target)) {
        dismiss().catch(console.error);
        event.preventDefault();
      }
    };
    onActivated(() => {
      document.addEventListener("keydown", onKey);
    });
    onDeactivated(() => {
      document.removeEventListener("keydown", onKey);
    });
    __expose({
      dismiss: shallowRef(dismiss),
      pop: shallowRef(dismiss)
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(buildClass.value),
        style: normalizeStyle(_ctx.style),
        onClick
      }, [
        createElementVNode("div", {
          ref_key: "mainContent",
          ref: mainContent
        }, [
          createElementVNode("div", _hoisted_1, [
            (openBlock(), createBlock(unref(ComponentWithPropertiesInstance), {
              key: _ctx.root.key,
              component: _ctx.root
            }, null, 8, ["component"]))
          ])
        ], 512)
      ], 6);
    };
  }
});
export {
  ComponentWithProperties,
  ComponentWithPropertiesInstance,
  FramedComponent,
  HistoryManager,
  _sfc_main$2 as ModalStackComponent,
  ModalStackComponentFinderMixin,
  NavigationController,
  NavigationMixin,
  _sfc_main as Popup,
  SplitViewController,
  _sfc_main$3 as StackComponent,
  UrlHelper,
  defineRoutes,
  extendUrl,
  forAllRoots,
  getExposeProxy,
  injectHooks,
  matchPath,
  matchQuery,
  normalizePushOptions,
  onCheckRoutes,
  onNotCheckRoutes,
  setTitle,
  setTitleSuffix,
  setUrl,
  templateToUrl,
  useCanDismiss,
  useCanPop,
  useCheckRoute,
  useCurrentComponent,
  useCurrentHref,
  useDismiss,
  useFocused,
  useManualPresent,
  useModalStackComponent,
  useNavigate,
  useNavigationController,
  usePop,
  usePopup,
  usePresent,
  useShow,
  useShowDetail,
  useSplitViewController,
  useUrl
};
